import { useNavigate } from 'react-router';
import { Loader2, UserPlus, Stethoscope, AlertTriangle, ArrowRight, ClipboardList } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetCsMetricsQuery } from '@/store/api/apiSlice';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'CS';
}

function formatCurrency(amount: number | undefined) {
    if (amount === undefined || amount === null || isNaN(amount)) return '₦0';
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        amount,
    );
}

function statusLabel(status: string) {
    return status
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

const STATUS_DOT: Record<string, string> = {
    new: 'bg-purple-500',
    pending: 'bg-yellow-500',
    awaiting_approval: 'bg-yellow-500',
    in_progress: 'bg-blue-500',
    under_review: 'bg-orange-500',
    completed: 'bg-green-500',
    cancelled: 'bg-red-500',
};

export default function CustomerSuccessDashboard() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);
    const { data: metrics, isLoading } = useGetCsMetricsQuery();

    const inv = metrics?.investigations;
    const earnings = metrics?.earnings;
    const statusEntries = inv?.byStatus ? Object.entries(inv.byStatus).filter(([, v]) => (v ?? 0) > 0) : [];

    const goToInvestigations = () => navigate('/customer-success/investigations');

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Customer Success'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'CS'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                                Welcome{currentUser?.firstName ? `, ${currentUser.firstName}` : ''}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Your investigations at a glance — act on what needs you, then dive in.
                            </p>
                        </div>
                        <button
                            onClick={goToInvestigations}
                            className="self-start sm:self-auto bg-[#f37b23] hover:bg-[#e06a15] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                        >
                            <ClipboardList className="h-4 w-4" />
                            View investigations
                        </button>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 text-[#FF6B35] animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Action items */}
                            <div>
                                <h2 className="text-sm font-bold text-gray-900 mb-3">Needs your attention</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                                    <ActionCard
                                        icon={<UserPlus className="h-5 w-5 text-amber-600" />}
                                        count={inv?.awaitingAgentAssignment ?? 0}
                                        label="Awaiting agent assignment"
                                        tone="amber"
                                        onClick={goToInvestigations}
                                    />
                                    <ActionCard
                                        icon={<Stethoscope className="h-5 w-5 text-rose-600" />}
                                        count={inv?.awaitingMedicalAssignment ?? 0}
                                        label="Awaiting medical assignment"
                                        tone="rose"
                                        onClick={goToInvestigations}
                                    />
                                    <ActionCard
                                        icon={<AlertTriangle className="h-5 w-5 text-orange-600" />}
                                        count={inv?.guarantorTownWarnings ?? 0}
                                        label="Guarantor town warnings"
                                        tone="orange"
                                        onClick={goToInvestigations}
                                    />
                                </div>
                            </div>

                            {/* Status breakdown + earnings */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Status breakdown */}
                                <div className="lg:col-span-2 rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-sm font-bold text-gray-900">Investigations by status</h2>
                                        <span className="text-2xl font-bold text-gray-900">{inv?.total ?? 0}</span>
                                    </div>
                                    {statusEntries.length === 0 ? (
                                        <p className="text-sm text-gray-400">No investigations yet.</p>
                                    ) : (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {statusEntries.map(([status, count]) => (
                                                <div
                                                    key={status}
                                                    className="rounded-xl bg-gray-50 px-4 py-3 flex flex-col gap-1"
                                                >
                                                    <span className="flex items-center gap-2 text-xs text-gray-500">
                                                        <span
                                                            className={`h-2 w-2 rounded-full ${STATUS_DOT[status] ?? 'bg-gray-400'}`}
                                                        />
                                                        {statusLabel(status)}
                                                    </span>
                                                    <span className="text-2xl font-bold text-gray-900">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Earnings */}
                                <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-sm font-bold text-gray-900">Your pay</h2>
                                        <button
                                            onClick={() => navigate('/earnings')}
                                            className="text-xs font-medium text-[#f37b23] hover:underline flex items-center gap-1"
                                        >
                                            Details <ArrowRight className="h-3 w-3" />
                                        </button>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <div className="rounded-xl bg-gray-50 px-4 py-3">
                                            <p className="text-xs text-gray-500">Total</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {formatCurrency(earnings?.total)}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="rounded-xl bg-green-50 px-4 py-3">
                                                <p className="text-xs text-green-600">Paid</p>
                                                <p className="text-lg font-bold text-green-900">
                                                    {formatCurrency(earnings?.paid)}
                                                </p>
                                            </div>
                                            <div className="rounded-xl bg-amber-50 px-4 py-3">
                                                <p className="text-xs text-amber-600">Pending</p>
                                                <p className="text-lg font-bold text-amber-900">
                                                    {formatCurrency(earnings?.pending)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}

function ActionCard({
    icon,
    count,
    label,
    tone,
    onClick,
}: {
    icon: React.ReactNode;
    count: number;
    label: string;
    tone: 'amber' | 'rose' | 'orange';
    onClick: () => void;
}) {
    const toneBg: Record<string, string> = {
        amber: 'bg-amber-50',
        rose: 'bg-rose-50',
        orange: 'bg-orange-50',
    };
    return (
        <button
            type="button"
            onClick={onClick}
            className="text-left rounded-2xl border border-gray-100 p-5 hover:border-gray-200 hover:shadow-sm transition-all flex items-center gap-4"
        >
            <div className={`h-11 w-11 shrink-0 rounded-xl ${toneBg[tone]} flex items-center justify-center`}>
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900 leading-none">{count}</p>
                <p className="text-xs text-gray-500 mt-1.5">{label}</p>
            </div>
        </button>
    );
}
